import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { List, X, CaretDown } from "@phosphor-icons/react";
import { t } from "../i18n";

const LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_25c443f8-0487-4dc0-bac7-262cfba10a96/artifacts/1dk8ijm2_Logo%20komplett.jpg";

const Logo = ({ className = "" }) => (
  <div className={`bg-navy-900 rounded-xl px-3 py-2 flex items-center ${className}`}>
    <img src={LOGO} alt="CLEANSORA Logo" className="h-8 md:h-9 w-auto object-contain" />
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [soonOpen, setSoonOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const c = t.de.nav;
  const isHome = loc.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setSoonOpen(false); }, [loc.pathname]);

  const goHome = (hash) => {
    if (loc.pathname !== "/") nav("/" + (hash ? `#${hash}` : ""));
    else if (hash) document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const links = [
    { label: c.home, action: () => goHome("") },
    { label: c.about, action: () => goHome("about") },
    { label: c.services, action: () => goHome("services") },
    { label: c.process, action: () => goHome("process") },
  ];

  const soonLinks = [
    { label: c.software, to: "/software" },
    { label: c.portal, to: "/kundenportal" },
    { label: c.app, to: "/mitarbeiter-app" },
  ];

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        solid ? "backdrop-blur-xl bg-white/85 border-b border-navy-900/5 py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="container mx-auto px-6 md:px-12 max-w-7xl flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => goHome("")} className="shrink-0" aria-label="CLEANSORA Start">
          <Logo />
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-${l.label}`}
              onClick={l.action}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-gold-400 ${
                solid ? "text-navy-900" : "text-white"
              }`}
            >
              {l.label}
            </button>
          ))}

          <div className="relative" onMouseEnter={() => setSoonOpen(true)} onMouseLeave={() => setSoonOpen(false)}>
            <button data-testid="nav-soon" className={`flex items-center gap-1 text-sm font-medium hover:text-gold-400 transition-colors ${solid ? "text-navy-900" : "text-white"}`}>
              {c.soon} <CaretDown size={14} weight="bold" className={`transition-transform ${soonOpen ? "rotate-180" : ""}`} />
            </button>
            {soonOpen && (
              <div className="absolute top-full right-0 pt-3">
                <div className="bg-white rounded-2xl shadow-[0_20px_40px_rgb(0,0,0,0.1)] border border-slate-100 p-2 w-56">
                  {soonLinks.map((s) => (
                    <Link
                      key={s.to}
                      to={s.to}
                      data-testid={`nav-${s.label}`}
                      className="block px-4 py-3 rounded-xl text-sm text-navy-900 hover:bg-gold-50 hover:text-gold-700 transition-colors"
                    >
                      {s.label}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-gold-500">bald</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/kontakt"
            data-testid="nav-quote-btn"
            className="bg-navy-900 text-white rounded-full px-6 py-3 text-sm font-medium transition-all hover:bg-gold-500 hover:-translate-y-0.5 hover:shadow-lg"
          >
            {c.quote}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-btn"
          className={`lg:hidden p-2 transition-colors ${solid || open ? "text-navy-900" : "text-white"}`}
          onClick={() => setOpen(!open)}
          aria-label="Menü"
        >
          {open ? <X size={26} /> : <List size={26} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-slate-100 mt-3">
          <div className="container mx-auto px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <button key={l.label} onClick={l.action} className="text-left py-3 text-navy-900 font-medium border-b border-slate-50">
                {l.label}
              </button>
            ))}
            {soonLinks.map((s) => (
              <Link key={s.to} to={s.to} className="py-3 text-navy-900 font-medium border-b border-slate-50">
                {s.label} <span className="text-[10px] uppercase tracking-wider text-gold-500">bald</span>
              </Link>
            ))}
            <Link to="/kontakt" className="mt-4 bg-navy-900 text-white rounded-full px-6 py-3 text-center font-medium">
              {c.quote}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export { Logo };
